import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Progress, Tooltip } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'
import { CompassOutlined, RightOutlined } from '@ant-design/icons'
import cn from 'classnames'

import {
  financeOnboardingSnoozedSelector,
  financeOnboardingStepSelector,
  isFinanceOnboardingEnabledSelector,
  mobileViewSelector,
} from 'app-redux/selectors'
import { useApp } from 'components/AppProvider'

import { GROUP_COUNT, STEPS } from './steps'
import styles from './financeOnboarding.module.scss'

// Widget "Panduan Memulai" di sidebar (di bawah logo) selama panduan ditunda lewat
// "Nanti saja". Menampilkan langkah & progres terakhir; klik membuka halaman Panduan Memulai
// (ringkasan langkah + tombol Lanjutkan), bukan langsung kembali ke step.
const GUIDE_PATH = '/getting-started'

const GuideWidget = ({ collapsed = false }) => {
  const history = useHistory()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { onChangeSettings } = useApp()
  const isMobileView = useSelector(mobileViewSelector)
  const step = useSelector(financeOnboardingStepSelector)
  const snoozed = useSelector(financeOnboardingSnoozedSelector)
  const enabled = useSelector(isFinanceOnboardingEnabledSelector)

  const handleOpen = useCallback(() => {
    if (isMobileView) {
      onChangeSettings({ isMobileMenuOpen: false })
    }
    if (history.location.pathname !== GUIDE_PATH) {
      history.push(GUIDE_PATH)
    }
  }, [history, isMobileView, onChangeSettings])

  const def = step ? STEPS[step] : null
  if (!snoozed || !def || !enabled) {
    return null
  }

  const group = Math.min(Math.max(def.group, 1), GROUP_COUNT)
  const stepLabel = formatMessage({ id: `financeOnboarding.rail_step${group}` })
  const counter = `${group}/${GROUP_COUNT}`
  const isActive = location.pathname === GUIDE_PATH

  if (collapsed) {
    return (
      <Tooltip
        placement="right"
        title={`${formatMessage({ id: 'financeOnboarding.guide_title' })} · ${counter}`}
      >
        <button
          type="button"
          className={cn(styles.guideMini, isActive && styles.guideMiniActive)}
          onClick={handleOpen}
        >
          <Progress
            type="circle"
            size={40}
            percent={def.pct}
            showInfo={false}
            strokeWidth={9}
            strokeColor="var(--sb-active-ink)"
            trailColor="var(--sb-line)"
          />
          <CompassOutlined className={styles.guideMiniIcon} />
        </button>
      </Tooltip>
    )
  }

  return (
    <div className={styles.guideWrap}>
      <button
        type="button"
        className={cn(styles.guide, isActive && styles.guideActive)}
        onClick={handleOpen}
      >
        <span className={styles.guideHead}>
          <span className={styles.guideIcon}>
            <CompassOutlined />
          </span>
          <span className={styles.guideTitle}>
            <FormattedMessage id="financeOnboarding.guide_title" />
          </span>
          <RightOutlined className={styles.guideArrow} />
        </span>
        <span className={styles.guideMeta}>
          <b>{counter}</b>
          <span>{stepLabel}</span>
        </span>
        <Progress
          percent={def.pct}
          showInfo={false}
          size="small"
          strokeColor="var(--sb-active-ink)"
          trailColor="var(--sb-line)"
        />
        <span className={styles.guideCta}>
          <FormattedMessage id="financeOnboarding.guide_resume" />
        </span>
      </button>
    </div>
  )
}

export default GuideWidget
